import type { Messages } from '@lingui/core';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { Fragment } from 'react';
// import { App } from './app';
import { EmailTenderingApp } from './app/EmailTendering';
import { SpaceSelection } from './app/SpaceSelection';
import { config } from './config';
import {
  CHOSEN_ELECTRON_SPACE,
  DETECT_TRANSLATIONS_IN_GQL_OPS,
  IS_ELECTRON,
} from './utils/constants';
import { fromEntries } from './utils/fromEntries';
import { initLD } from './utils/initLaunchDarkly';
import { mark } from './utils/perf';
import { renderRoot } from './utils/render';
import { init as initSentry } from './utils/sentry';
import { win } from './utils/win';

import('./config/tenants/loader')
  .then((mod) => {
    const { getCurrentEnvironment } = mod;
    const current = getCurrentEnvironment();
    win['masteryEnvV2'] = current;
  })
  .catch((e) => {
    // eslint-disable-next-line no-console
    console.error('Error loading tenants config', e);
  });

mark('src/index/init');

const defaultLocale = localStorage.getItem('locale') || 'en-US';
type Msg = string | Array<string>;
type Catalog = Record<string, Array<Msg>>;

/**
 * We do a dynamic import of just the catalog that we need
 * @param locale any locale string
 */
async function dynamicActivate(rawLocale: string): Promise<void> {
  // Default to 'en-US' if legacy 'en' is found.
  const locale = rawLocale === 'en' ? 'en-US' : rawLocale;

  const imported = await import(`./locales/${locale}.po`);
  let messages: Catalog = imported.messages;
  if (DETECT_TRANSLATIONS_IN_GQL_OPS) {
    // add a &#8203; zero-width space to each string
    messages = fromEntries(
      Object.entries(messages).map(([key, value]) => {
        const newValue = value.map((msg) =>
          typeof msg === 'string'
            ? msg + '\u200B'
            : // do not modify nested strings, they are for vars and plurals
              msg,
        );
        return [key, newValue];
      }),
    );
  }

  // Cast the imported messages to the Lingui Messages type so i18n.load accepts them.
  const loadedMessages = messages as unknown as Messages;

  i18n.load(locale, loadedMessages);
  i18n.activate(locale);
}

// This is to make fragment rendering work...jsxFactory issues with tsconfig
win.React = {
  Fragment,
};

initSentry();

const isEmailTendering = win.location.pathname === '/email-tender';

if (IS_ELECTRON && !CHOSEN_ELECTRON_SPACE) {
  /* istanbul ignore next */
  renderRoot(<SpaceSelection />);
} else if (isEmailTendering) {
  (async (): Promise<void> => {
    await dynamicActivate(defaultLocale);
    renderRoot(
      <I18nProvider i18n={i18n}>
        <EmailTenderingApp />
      </I18nProvider>,
    );
  })();
} else {
  (async (): Promise<void> => {
    await dynamicActivate(defaultLocale);
    const LDProvider = await initLD({
      launchDarklyKey: config.launchDarklyKey,
    });
    const { App } = await import('./app');
    mark('src/index/render');
    renderRoot(
      <LDProvider>
        <I18nProvider i18n={i18n}>
          <App />
        </I18nProvider>
      </LDProvider>,
    );
  })();
}
